import {
  CompetitionLevel,
  getOrganizationsSearch,
} from "@aura-frontend/omnibus-az-client";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import {
  Button,
  cn,
  Icons,
  Input,
  Select,
  Table,
  useDebounce,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import AccountCreationModal from "@/components/account-creation-modal";
import { competitionLevelsOptions } from "@/lib/competition-level-options";
import { sportsOptions } from "@/lib/sports-options";

import { useColumns } from "./Organizations.columns";

const compLevelItems = [
  { value: "all-comp", label: "All Levels" },
  ...competitionLevelsOptions,
];

const sportItems = [
  { value: "all-sports", label: "All Sports" },
  ...sportsOptions,
];

const organizationsInfiniteQueryOptions = (
  competitionLevel?: CompetitionLevel,
  sport?: "baseball" | "softball",
  name?: string,
) =>
  infiniteQueryOptions({
    queryKey: ["getOrganizationsSearch", competitionLevel, sport, name],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getOrganizationsSearch({
        query: {
          ...(competitionLevel && { competitionLevel }),
          ...(sport && { sport }),
          ...(name && { name }),
          continue: pageParam,
        },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
  });

const Organizations = () => {
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);
  const [selectedSport, setSelectedSport] = useState<
    "baseball" | "softball" | undefined
  >();
  const [selectedComp, setSelectedComp] = useState<CompetitionLevel>();
  const [isAccountCreationModalOpen, setIsAccountCreationModalOpen] =
    useState(false);
  const [sortingState, setSortingState] = useState<SortingState>([]);

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery(
    organizationsInfiniteQueryOptions(selectedComp, selectedSport, searchValue),
  );

  const organizations = useMemo(
    () => data?.pages.flatMap((page) => page.organizations) ?? [],
    [data?.pages],
  );

  const { columns } = useColumns();

  return (
    <div className="relative flex flex-col">
      <div
        className={cn(
          "flex flex-col px-5 gap-4 items-start",
          "lg:flex-row-reverse lg:items-center lg:justify-between",
        )}
      >
        <Button
          variant="secondary-flat-light"
          size="sm"
          onClick={() => setIsAccountCreationModalOpen(true)}
        >
          <Icons.Plus className="size-4" />
          Add Organization
        </Button>

        <div
          className={cn(
            "flex flex-col-reverse  gap-4 items-start",
            "md:flex-row md:items-end md:justify-between",
          )}
        >
          <div className="flex-1 min-w-0">
            <Select
              items={compLevelItems}
              value={selectedComp ?? "all-comp"}
              onValueChange={(value) =>
                setSelectedComp(
                  value === "all-comp"
                    ? undefined
                    : (value as CompetitionLevel),
                )
              }
              placeholder="Comp Level"
            />
          </div>
          <div className="flex-1 min-w-0">
            <Select
              items={sportItems}
              value={selectedSport ?? "all-sports"}
              onValueChange={(value) => {
                setSelectedSport(
                  value === "all-sports"
                    ? undefined
                    : (value.toLowerCase() as "baseball" | "softball"),
                );
              }}
              placeholder="Sport"
            />
          </div>
          <div className="flex-1 min-w-0">
            <Input
              value={rawSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search"
              isClearable
              onClear={() => setSearchValue("")}
              iconRight={
                <Icons.MagnifyingGlass className="h-5 w-5 text-placeholder" />
              }
            />
          </div>
        </div>
      </div>

      <AccountCreationModal
        open={isAccountCreationModalOpen}
        onOpenChange={setIsAccountCreationModalOpen}
        refresh={() => {
          refetch();
        }}
      />

      <div className="mt-8">
        <Table
          enableHorizontalScroll
          variant="infinite-scroll"
          hasMore={hasNextPage}
          isFetching={isLoading || isFetchingNextPage}
          data={organizations}
          columns={columns}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          rowClassName={"shadow-glow-tiny rounded"}
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto px-5 py-1"
        />
      </div>
    </div>
  );
};

export { Organizations };
